package org.multiplatform.kotfolio.sections

import androidx.compose.runtime.Composable
import org.multiplatform.kotfolio.components.CodeWindow
import org.multiplatform.kotfolio.models.Section
import org.multiplatform.kotfolio.util.Constants
import com.varabyte.kobweb.compose.css.*
import com.varabyte.kobweb.compose.foundation.layout.Arrangement
import com.varabyte.kobweb.compose.foundation.layout.Box
import com.varabyte.kobweb.compose.foundation.layout.Column
import com.varabyte.kobweb.compose.foundation.layout.Row
import com.varabyte.kobweb.compose.ui.Alignment
import com.varabyte.kobweb.compose.ui.Modifier
import com.varabyte.kobweb.compose.ui.graphics.Color
import com.varabyte.kobweb.compose.ui.graphics.Colors
import com.varabyte.kobweb.compose.ui.modifiers.*
import com.varabyte.kobweb.compose.ui.styleModifier
import com.varabyte.kobweb.compose.ui.toAttrs
import com.varabyte.kobweb.navigation.OpenLinkStrategy
import com.varabyte.kobweb.silk.components.layout.SimpleGrid
import com.varabyte.kobweb.silk.components.layout.numColumns
import com.varabyte.kobweb.silk.components.navigation.Link
import com.varabyte.kobweb.silk.style.breakpoint.Breakpoint
import org.jetbrains.compose.web.css.*
import org.jetbrains.compose.web.dom.P
import org.jetbrains.compose.web.dom.Span
import org.jetbrains.compose.web.dom.Text
import org.multiplatform.kotfolio.rememberAppState
import org.multiplatform.kotfolio.styles.CanvasBackground

// Colors from ReferenceCode.md
private val AMBER_100 = Color.rgb(254, 243, 199)
private val AMBER_500 = Color.rgb(245, 158, 11)
private val AMBER_50 = Color.rgba(255, 251, 235, 0.8f)
private val BROWN_BG = Color.rgb(44, 25, 16)
private val PRIMARY_BROWN = Color.rgb(107, 66, 38)

@Composable
fun mainSection(onMenuClicked: () -> Unit) {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    Box(
        modifier = Modifier
            .id(Section.Home.id)
            .fillMaxWidth()
            .backgroundColor(BROWN_BG),
        contentAlignment = Alignment.TopCenter,
    ) {
        CanvasBackground()

        Box(
            modifier = Modifier
                .fillMaxWidth()
                .maxWidth(Constants.SECTION_WIDTH.px)
                .styleModifier {
                    // Primary background: Left-to-right fade with 0.1 alpha as requested
                    if (isDesktop) {
                        property(
                            "background-image",
                            "linear-gradient(to right, rgba(44, 25, 16, 1) 0%, rgba(44, 25, 16, 0.9) 30%, rgba(44, 25, 16, 0.1) 100%), url('hero_bg.jpg')",
                        )
                    } else {
                        property(
                            "background-image",
                            "linear-gradient(to bottom, rgba(44, 25, 16, 1) 0%, rgba(44, 25, 16, 0.1) 100%), url('hero_bg.jpg')",
                        )
                    }
                    property("background-size", "cover")
                    property("background-position", if (isDesktop) "center right" else "center")
                    property("background-repeat", "no-repeat")
                },
            contentAlignment = Alignment.TopCenter,
        ) {
            mainContent()

            // Bottom Fade Layer: Ultra-smooth 400px transition
            Box(
                modifier = Modifier
                    .fillMaxWidth()
                    .height(400.px)
                    .align(Alignment.BottomCenter)
                    .styleModifier {
                        property("background", "linear-gradient(to bottom, transparent 0%, rgba(44, 25, 16, 0.3) 40%, rgba(44, 25, 16, 0.8) 80%, rgba(44, 25, 16, 1) 100%)")
                    },
            )
        }
    }
}


@Composable
private fun mainContent() {
    val breakpoint = rememberAppState().breakpoint
    val isDesktop = breakpoint >= Breakpoint.MD

    Column(
        modifier = Modifier.fillMaxWidth().minHeight(100.vh),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        // Spacer for fixed header
        Box(modifier = Modifier.height(100.px))

        SimpleGrid(
            modifier = Modifier
                .fillMaxWidth(if (isDesktop) 90.percent else 95.percent)
                .padding(bottom = if (isDesktop) 150.px else 100.px),
            numColumns = numColumns(base = 1, md = 2),
        ) {
            mainText(breakpoint)
            if (isDesktop) mainImage()
        }
    }
}

@Composable
private fun mainText(breakpoint: Breakpoint) {
    val isDesktop = breakpoint >= Breakpoint.MD
    val isLarge = breakpoint >= Breakpoint.LG

    Column(
        modifier = Modifier
            .fillMaxWidth()
            .padding(
                top = if (isDesktop) 40.px else 24.px,
                bottom = if (isDesktop) 20.px else 16.px,
                leftRight = if (isDesktop) 40.px else 0.px,
            ),
        horizontalAlignment = if (isDesktop) Alignment.Start else Alignment.CenterHorizontally,
        verticalArrangement = Arrangement.Center,
    ) {
        // Hello World Snippet badge
        Row(
            modifier = Modifier
                .margin(bottom = if (isDesktop) 32.px else 24.px)
                .padding(leftRight = 16.px, topBottom = 10.px)
                .backgroundColor(Color.rgba(245, 158, 11, 0.15f))
                .borderRadius(24.px)
                .border(1.px, LineStyle.Solid, Color.rgba(245, 158, 11, 0.3f)),
            verticalAlignment = Alignment.CenterVertically,
        ) {
            Span(
                attrs = Modifier
                    .color(AMBER_500)
                    .fontSize(if (isDesktop) 14.px else 12.px)
                    .fontWeight(FontWeight.Bold)
                    .fontFamily("Courier New", "monospace")
                    .toAttrs(),
            ) { Text("System.out.println(\"Kotlin, Multiplatform Developer!\");") }
        }

        // Intro Headline (Replacing Name)
        P(
            attrs = Modifier
                .margin(top = 24.px, bottom = 0.px)
                .fontFamily(Constants.FONT_FAMILY)
                .fontSize(
                    if (isLarge) {
                        50.px
                    } else if (isDesktop) {
                        44.px
                    } else {
                        32.px
                    },
                )
                .fontWeight(FontWeight.Black)
                .color(Colors.White)
                .lineHeight(1.2)
                .textAlign(if (isDesktop) TextAlign.Start else TextAlign.Center)
                .toAttrs(),
        ) { Text(Constants.MAIN_INTRO.first()) }

        // Role line with dash
        Row(
            verticalAlignment = Alignment.CenterVertically,
            modifier = Modifier
                .margin(
                    top = if (isDesktop) 24.px else 16.px,
                    bottom = if (isDesktop) 24.px else 16.px,
                )
                .fillMaxWidth(),
            horizontalArrangement = if (isDesktop) Arrangement.Start else Arrangement.Center,
        ) {
            Box(
                modifier = Modifier
                    .styleModifier { property("width", "32px") }
                    .height(2.px)
                    .backgroundColor(AMBER_500)
                    .margin(right = 12.px),
            )
            P(
                attrs = Modifier
                    .margin(topBottom = 0.px)
                    .fontFamily(Constants.FONT_FAMILY)
                    .fontSize(if (isDesktop) 28.px else 22.px)
                    .fontWeight(com.varabyte.kobweb.compose.css.FontWeight.Bold)
                    .color(AMBER_500)
                    .toAttrs(),
            ) { Text(Constants.ROLE.replace("Current: ", "")) }
        }

        // No redundant paragraph needed as headline covers it

        // No longer needed here as it has been moved to About Me

        // On mobile, show the CodeWindow below the text
        if (!isDesktop) {
            Box(modifier = Modifier.height(48.px))
            mainImage()
        }
    }
}

@Composable
private fun socialIcon(path: String, content: @Composable () -> Unit) {
    Link(
        path = path,
        openExternalLinksStrategy = OpenLinkStrategy.IN_NEW_TAB,
        modifier = Modifier
            .color(AMBER_500)
            .transition(Transition.of("opacity", duration = 200.ms)),
    ) {
        content()
    }
}

@Composable
private fun mainImage() {
    Column(
        modifier = Modifier.fillMaxSize().padding(all = 10.px),
        verticalArrangement = Arrangement.Center,
        horizontalAlignment = Alignment.CenterHorizontally,
    ) {
        CodeWindow()
    }
}
